import React from 'react';
import { Wrench, Play, RefreshCw, Server } from 'lucide-react';

const ToolPanel = ({ tools = [], servers = [], onCallTool, onRefreshTools }) => {
  const connectedServers = servers.filter(s => s.connected);

  const handleCallTool = (toolName) => {
    if (connectedServers.length === 0) {
      return;
    }
    
    const serverName = connectedServers[0].name;
    onCallTool(toolName, serverName, {});
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Available Tools</h2>
            <p className="text-sm text-gray-500 mt-1">
              Tools from connected MCP servers
            </p>
          </div>
          <button
            onClick={onRefreshTools}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Connection Status */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Server className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-blue-800">Server Status</span>
          </div>
          <p className="text-sm text-blue-700">
            {connectedServers.length === 0 
              ? 'No servers connected. Go to Servers panel to connect.'
              : `${connectedServers.length} server(s) connected, ${tools.length} tools available`
            }
          </p>
        </div>

        {/* Tools List */}
        {tools.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Wrench className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No Tools Available</h3>
            <p className="text-sm">
              {connectedServers.length === 0 
                ? 'Connect to an MCP server to see available tools'
                : 'Click "Refresh" to load tools from connected servers'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {tools.map((tool, index) => (
              <div
                key={`${tool.name}-${index}`}
                className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-800">{tool.name}</h3>
                      {tool.serverName && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {tool.serverName}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {tool.description || 'No description available'}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => handleCallTool(tool.name)}
                    disabled={connectedServers.length === 0}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white rounded-md transition-colors text-sm"
                  >
                    <Play className="w-3 h-3" />
                    Test
                  </button>
                </div>
                
                {tool.inputSchema && (
                  <details className="mt-3">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 select-none">
                      View Schema
                    </summary>
                    <div className="mt-2 p-3 bg-gray-50 rounded border text-xs">
                      <pre className="text-gray-700 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(tool.inputSchema, null, 2)}
                      </pre>
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolPanel;